import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Trophy, Brain, Cards, CheckCircle, XCircle, ArrowClockwise, Star, Crown } from "@phosphor-icons/react";

export default function Trivia() {
  const [pairs, setPairs] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [memorama, setMemorama] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);

  const loadData = useCallback(() => {
    api.get("/trivia/pairs").then(({ data }) => setPairs(data));
    api.get("/trivia/memorama").then(({ data }) => setMemorama(data));
    api.get("/trivia/leaderboard").then(({ data }) => setLeaderboard(data));
  }, []);
  useEffect(() => { loadData(); loadQuiz(); }, [loadData]);

  const loadQuiz = () => {
    api.get("/trivia/questions?limit=15").then(({ data }) => { setQuestions(data); setAnswers({}); setResult(null); });
  };

  const submit = async () => {
    if (Object.keys(answers).length < questions.length) { toast.error(`Responde las ${questions.length} preguntas`); return; }
    try {
      // Send the questions along so the backend can grade dynamic ones too
      const qPayload = questions.map((q) => ({ id: q.id, answer: q.answer }));
      const { data } = await api.post("/trivia/submit", { answers, questions: qPayload });
      setResult(data);
      toast.success(`Score: ${data.score_pct}% (${data.correct}/${data.total})`);
      api.get("/trivia/leaderboard").then(({ data }) => setLeaderboard(data));
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="trivia-page">
      <div>
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">Premier Padel</p>
        <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-black uppercase tracking-tighter">Juegos y Trivia</h1>
        <p className="text-sm text-gray-400 mt-1">Retate con preguntas de la elite del pádel y ejercita tu memoria</p>
      </div>

      <Tabs defaultValue="quiz" className="w-full">
        <TabsList className="bg-[#141414] border border-white/10">
          <TabsTrigger value="quiz" className="data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black uppercase text-xs font-bold tracking-wider" data-testid="tab-quiz"><Brain size={14} className="mr-1" /> Quiz</TabsTrigger>
          <TabsTrigger value="pairs" className="data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black uppercase text-xs font-bold tracking-wider" data-testid="tab-pairs"><Crown size={14} className="mr-1" /> Top 16</TabsTrigger>
          <TabsTrigger value="memorama" className="data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black uppercase text-xs font-bold tracking-wider" data-testid="tab-memorama"><Cards size={14} className="mr-1" /> Memorama</TabsTrigger>
          <TabsTrigger value="ranking" className="data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black uppercase text-xs font-bold tracking-wider" data-testid="tab-trivia-ranking"><Trophy size={14} className="mr-1" /> Ranking</TabsTrigger>
        </TabsList>

        <TabsContent value="quiz" className="mt-4">
          <div className="card-tactical p-6 space-y-4" data-testid="quiz-section">
            <div className="flex items-center justify-between">
              <h2 className="font-['Barlow_Condensed'] text-2xl uppercase">Quiz del Padel</h2>
              <Button onClick={loadQuiz} size="sm" variant="outline" className="border-white/10 text-xs uppercase" data-testid="quiz-reset"><ArrowClockwise size={14} className="mr-1" /> Nueva ronda</Button>
            </div>
            {questions.map((q, i) => (
              <div key={q.id} className="p-4 bg-[#0A0A0A] border border-white/10" data-testid={`quiz-q-${q.id}`}>
                <p className="text-sm text-white mb-3"><span className="text-[#CCFF00] font-bold">#{i + 1}</span> {q.q}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {q.options.map((opt, idx) => {
                    const selected = answers[q.id] === idx;
                    const isCorrect = result && result.breakdown?.find(b => b.id === q.id)?.correct_answer === idx;
                    const wasChosen = selected;
                    const cls = result
                      ? (isCorrect ? "border-green-500/60 bg-green-500/10 text-green-300" : wasChosen ? "border-red-500/60 bg-red-500/10 text-red-300" : "border-white/10 text-gray-500")
                      : (selected ? "border-[#CCFF00] bg-[#CCFF00]/10 text-[#CCFF00]" : "border-white/10 text-gray-300 hover:border-[#CCFF00]/30");
                    return (
                      <button
                        key={idx}
                        disabled={!!result}
                        onClick={() => setAnswers((p) => ({ ...p, [q.id]: idx }))}
                        className={`text-left p-3 text-xs border transition-colors ${cls}`}
                        data-testid={`quiz-q-${q.id}-opt-${idx}`}
                      >
                        {opt} {result && isCorrect && <CheckCircle size={12} className="inline ml-1" />}
                        {result && wasChosen && !isCorrect && <XCircle size={12} className="inline ml-1" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            {!result ? (
              <Button onClick={submit} className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="quiz-submit">Enviar respuestas</Button>
            ) : (
              <div className="p-4 bg-[#CCFF00]/10 border border-[#CCFF00]/30 text-center" data-testid="quiz-result">
                <p className="font-['Barlow_Condensed'] text-3xl font-black text-[#CCFF00]">{result.score_pct}%</p>
                <p className="text-xs text-gray-400 uppercase tracking-widest mt-1">{result.correct} de {result.total} correctas</p>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="pairs" className="mt-4">
          <div className="card-tactical p-6" data-testid="pairs-section">
            <h2 className="font-['Barlow_Condensed'] text-2xl uppercase mb-4">Top 16 Parejas Premier Padel 2025</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {pairs.map((p) => (
                <div key={p.rank} className="bg-[#0A0A0A] border border-white/10 p-3 flex items-center gap-3" data-testid={`pair-${p.rank}`}>
                  <div className={`w-10 h-10 flex items-center justify-center font-['Barlow_Condensed'] text-xl font-black ${p.rank <= 3 ? "bg-[#CCFF00]/10 text-[#CCFF00]" : "bg-white/5 text-gray-400"}`}>#{p.rank}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-white truncate">{p.p1} / {p.p2}</p>
                    <p className="text-[10px] text-gray-500">{p.country} · {p.titles} titulos</p>
                  </div>
                  {p.rank <= 3 && <Star size={16} weight="fill" className="text-[#CCFF00]" />}
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="memorama" className="mt-4">
          <Memorama items={memorama} />
        </TabsContent>

        <TabsContent value="ranking" className="mt-4">
          <div className="card-tactical p-6" data-testid="trivia-ranking-section">
            <h2 className="font-['Barlow_Condensed'] text-2xl uppercase mb-4">Mejores puntuaciones</h2>
            {leaderboard.length === 0 ? (
              <p className="text-xs text-gray-500">Aun no hay resultados. Juega el quiz!</p>
            ) : (
              <div className="space-y-2">
                {leaderboard.slice(0, 20).map((r, i) => (
                  <div key={r.id} className={`flex items-center gap-3 p-3 border ${i < 3 ? "bg-green-500/5 border-green-500/20" : "bg-[#0A0A0A] border-white/10"}`} data-testid={`leaderboard-${i}`}>
                    <span className={`font-['Barlow_Condensed'] text-xl font-black ${i < 3 ? "text-green-400" : "text-gray-600"}`}>#{i + 1}</span>
                    <span className="flex-1 text-sm font-semibold text-white">{r.user_name}</span>
                    <Badge variant="outline" className="bg-[#CCFF00]/10 text-[#CCFF00] border-[#CCFF00]/30">{r.score_pct}%</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Memorama({ items }) {
  const [cards, setCards] = useState([]);
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [moves, setMoves] = useState(0);

  const reset = useCallback(() => {
    if (!items.length) return;
    const double = [...items, ...items].map((it, i) => ({ ...it, key: `${it.id}-${i}` }));
    for (let i = double.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [double[i], double[j]] = [double[j], double[i]];
    }
    setCards(double);
    setFlipped([]);
    setMatched([]);
    setMoves(0);
  }, [items]);

  useEffect(() => { reset(); }, [reset]);

  const handleFlip = (i) => {
    if (flipped.length >= 2) return;
    if (flipped.includes(i) || matched.includes(cards[i].id)) return;
    const next = [...flipped, i];
    setFlipped(next);
    if (next.length === 2) {
      setMoves((m) => m + 1);
      const [a, b] = next;
      if (cards[a].id === cards[b].id) {
        setTimeout(() => { setMatched((p) => [...p, cards[a].id]); setFlipped([]); }, 450);
      } else {
        setTimeout(() => setFlipped([]), 800);
      }
    }
  };

  const won = matched.length === items.length && items.length > 0;

  return (
    <div className="card-tactical p-6" data-testid="memorama-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="font-['Barlow_Condensed'] text-2xl uppercase">Memorama de Padel</h2>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-400 uppercase tracking-widest">Intentos: <span className="text-[#CCFF00] font-bold font-['JetBrains_Mono']">{moves}</span></span>
          <Button onClick={reset} size="sm" variant="outline" className="border-white/10 text-xs uppercase" data-testid="memorama-reset"><ArrowClockwise size={14} className="mr-1" /> Reiniciar</Button>
        </div>
      </div>
      {won && (
        <div className="mb-4 p-4 bg-green-500/10 border border-green-500/30 text-center" data-testid="memorama-won">
          <p className="font-['Barlow_Condensed'] text-2xl font-black text-green-400">Completado!</p>
          <p className="text-xs text-gray-400 uppercase tracking-widest mt-1">En {moves} intentos</p>
        </div>
      )}
      <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
        {cards.map((c, i) => {
          const show = flipped.includes(i) || matched.includes(c.id);
          return (
            <button
              key={c.key}
              onClick={() => handleFlip(i)}
              className={`aspect-square flex flex-col items-center justify-center border-2 transition-transform duration-300 ${show ? "bg-[#CCFF00]/10 border-[#CCFF00]" : "bg-[#0A0A0A] border-white/10 hover:border-[#CCFF00]/30"}`}
              data-testid={`memorama-card-${i}`}
            >
              {show ? (
                <>
                  <span className="text-2xl sm:text-3xl">{c.icon}</span>
                  <span className="text-[9px] text-[#CCFF00] mt-1 uppercase tracking-wider">{c.label}</span>
                </>
              ) : (
                <span className="text-2xl text-gray-700">?</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
